<template>
  <div
    class="filter filter--primary"
    :class="{ 'filter--first': isFirstFilter, 'is-open': isFilterOpen }"
  >
    <transition name="fade">
      <span class="filter__label" v-show="isMobile && !isFilterOpen">{{ filter.title }}</span>
    </transition>
    <div class="filter__title" @click="toggleFilter()">
      {{ selectedLabel }}
    </div>
    <Expandable
      class="filter__values"
      :isOpen="isFilterOpen"
      @height-change="onAccordionHeightChange"
      @height-change-end="onAccordionHeightChangeEnd"
    >
      <div
        v-for="option in filter.options"
        v-show="!selectedValues.includes(option.value)"
        :key="option.value"
        class="filter__value"
        @click="selectFilter(option)"
      >
        {{ option.label }}
      </div>
    </Expandable>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { GTM_EVENT_CATEGORIES } from '@/services/external-communication/gtm/gtm-events'
import Expandable from '@/components/_shared/Expandable.vue'

export default {
  name: 'PrimaryFilter',
  components: {
    Expandable,
  },

  props: {
    filter: {
      type: Object,
      required: true,
    },

    isFirstFilter: {
      type: Boolean,
      required: false,
    },
  },

  data() {
    return {
      isExpandableOpen: false,
      isFilterOpen: false,
      isFilterValueOpen: false,
    }
  },

  computed: {
    ...mapGetters({
      isMobile: 'filter/isMobile',
      activePrimaryFilters: 'filter/activePrimaryFilters',
    }),

    selectedFilter() {
      return this.activePrimaryFilters.find(active => active.id === this.filter.id)
    },

    defaultLabel() {
      return this.filter.options.find(option => option.value === '')?.label || ''
    },

    selectedLabel() {
      if (!this.selectedFilter) return this.defaultLabel

      return this.selectedFilter.values
        .map(value => this.filter.options.find(option => option.value === value).label)
        .join(', ')
    },

    selectedValues() {
      return this.selectedFilter?.values || ['']
    },
  },

  watch: {
    async isFilterOpen(isOpen) {
      // Displaying the filter value needs to happen before expanding the accordion to avoid lag
      // in the transition but needs to happen one tick later when closing the accordion to let
      // the component calculate the height needed for the transition (otherwise, there is no
      // transition at all).
      isOpen ? (this.isFilterValueOpen = isOpen) : (this.isExpandableOpen = isOpen)
      await this.$nextTick()
      isOpen ? (this.isExpandableOpen = isOpen) : (this.isFilterValueOpen = isOpen)
    },
  },

  methods: {
    ...mapActions({
      setPrimaryFilter: 'filter/setPrimaryFilter',
      removePrimaryFilter: 'filter/removePrimaryFilter',
    }),

    selectFilter(option) {
      this.isFilterOpen = false

      if (option.value === '') {
        this.removePrimaryFilter({
          ...this.filter,
          value: option.value,
        })
      } else {
        this.setPrimaryFilter({
          ...this.filter,
          values: [option.value],
        })
      }

      this.$notifier.trackEvent({
        action: this.$notifier.ACTIONS.filterAndSorting,
        details: {
          category: GTM_EVENT_CATEGORIES.filterSorting,
          action: this.filter.id,
          label: option.value || 'all',
        },
      })
    },

    toggleFilter() {
      this.isFilterOpen = !this.isFilterOpen
      if (this.isFilterOpen) {
        this.$notifier.trackEvent({
          action: this.$notifier.ACTIONS.filterAndSorting,
          details: {
            category: GTM_EVENT_CATEGORIES.filterSorting,
            action: this.filter.id,
            label: 'expand',
          },
        })
      }
    },

    onAccordionHeightChange(additionalHeight) {
      this.$emit('height-change', additionalHeight)
    },

    onAccordionHeightChangeEnd() {
      this.$emit('height-change-end')
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../Filter';
</style>
